import React, { useState } from 'react';
import useTranslationFeedback from '../hooks/useTranslationFeedback';
import useTargetLocales from '../hooks/useTargetLocales';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../components/ui/table';

// Phase 22 — 'typo' dropped; 3 tiers now drive leaderboard points (5/3/1).
const SEVERITIES = [
  { value: 'blocker', label: 'Blocker', points: 5 },
  { value: 'major',   label: 'Major',   points: 3 },
  { value: 'minor',   label: 'Minor',   points: 1 },
];

const SEVERITY_BADGE = {
  blocker: 'bg-red-100 text-red-800 border-red-200',
  major:   'bg-orange-100 text-orange-800 border-orange-200',
  minor:   'bg-yellow-100 text-yellow-800 border-yellow-200',
};

const STATUS_BADGE = {
  open:     'bg-gray-100 text-gray-800 border-gray-200',
  applied:  'bg-emerald-100 text-emerald-800 border-emerald-200',
  rejected: 'bg-zinc-100 text-zinc-600 border-zinc-200',
};

const SOURCE_BADGE = {
  storefront_widget: 'bg-violet-100 text-violet-800 border-violet-200',
  admin:             'bg-slate-100 text-slate-700 border-slate-200',
};
const SOURCE_LABEL = {
  storefront_widget: 'Storefront',
  admin:             'Admin',
};

function relTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

export default function Inbox() {
  const { locales } = useTargetLocales();
  const fb = useTranslationFeedback();
  const [busyId, setBusyId] = useState(null);
  // Per-row severity-picker open state. Holds the row id currently being decided
  // on, so only one picker is visible at a time.
  const [pickingForId, setPickingForId] = useState(null);

  const handleReject = async (id) => {
    const notes = window.prompt('Reason for rejecting (optional, shown to reporter)?') || null;
    setBusyId(id);
    try {
      await fb.decide(id, 'rejected', notes);
    } catch (err) {
      alert(`Failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const handleAcceptWithSeverity = async (id, severity) => {
    setBusyId(id);
    setPickingForId(null);
    try {
      await fb.decide(id, 'applied', null, severity);
    } catch (err) {
      alert(`Failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const countByStatus = (s) => fb.counts
    .filter((c) => c.status === s)
    .reduce((sum, c) => sum + Number(c.n || 0), 0);

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Inbox</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Translation feedback from the storefront widget. Accept to award points
          to the reporter (severity drives the leaderboard).
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex gap-1">
            {['open', 'applied', 'rejected', ''].map((s) => (
              <Button
                key={s || 'all'}
                variant={fb.statusFilter === s ? 'default' : 'outline'}
                size="sm"
                onClick={() => fb.setStatusFilter(s)}
              >
                {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
                {s && (
                  <span className="ml-2 text-xs opacity-70">{countByStatus(s)}</span>
                )}
              </Button>
            ))}
          </div>
          <div className="ml-auto flex gap-1">
            {['', ...locales].map((l) => (
              <Button
                key={l || 'all-loc'}
                variant={fb.localeFilter === l ? 'default' : 'outline'}
                size="sm"
                onClick={() => fb.setLocaleFilter(l)}
              >
                {l ? l.toUpperCase() : 'All'}
              </Button>
            ))}
          </div>
          <div className="flex gap-1">
            {['', 'storefront_widget', 'admin'].map((s) => (
              <Button
                key={s || 'all-src'}
                variant={fb.sourceFilter === s ? 'default' : 'outline'}
                size="sm"
                onClick={() => fb.setSourceFilter(s)}
              >
                {s === '' ? 'All sources' : SOURCE_LABEL[s]}
              </Button>
            ))}
          </div>
        </div>

        {fb.loading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {fb.error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {fb.error}
          </div>
        )}

        {!fb.loading && !fb.error && fb.entries.length === 0 && (
          <div className="rounded-md border bg-muted/30 px-4 py-8 text-center text-sm text-muted-foreground">
            No reports match these filters.
          </div>
        )}

        {!fb.loading && !fb.error && fb.entries.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">Locale</TableHead>
                <TableHead className="w-24">Severity</TableHead>
                <TableHead className="w-28">Source</TableHead>
                <TableHead>Current → Suggested</TableHead>
                <TableHead className="w-40">Page</TableHead>
                <TableHead className="w-32">Reporter</TableHead>
                <TableHead className="w-24">Status</TableHead>
                <TableHead className="w-56">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fb.entries.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="font-mono text-xs uppercase">{e.locale}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={SEVERITY_BADGE[e.severity]}>
                      {e.severity}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={SOURCE_BADGE[e.source] || SOURCE_BADGE.admin}>
                      {SOURCE_LABEL[e.source] || SOURCE_LABEL.admin}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-md">
                    <div className="text-sm">
                      <span className="text-muted-foreground line-through">{e.current_text}</span>
                    </div>
                    <div className="text-sm font-medium">
                      → {e.suggested_text}
                    </div>
                    {e.notes && (
                      <div className="text-xs text-muted-foreground mt-1">{e.notes}</div>
                    )}
                    {e.where_on_page && (
                      <div className="text-xs text-muted-foreground mt-1">
                        <span className="font-medium">where:</span> {e.where_on_page}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    {e.page_label && <div>{e.page_label}</div>}
                    {e.page_url && (
                      <a
                        href={e.page_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline break-all"
                      >
                        {e.page_url.length > 35 ? `${e.page_url.slice(0, 35)}…` : e.page_url}
                      </a>
                    )}
                    {e.source === 'storefront_widget' && e.page_context && (
                      <pre className="mt-2 text-[10px] font-mono bg-muted/40 p-2 rounded max-h-32 overflow-auto whitespace-pre-wrap break-all">
                        {e.page_context}
                      </pre>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    <div>{e.reporter_name || e.reporter_email || 'Anonymous'}</div>
                    <div className="text-muted-foreground">{relTime(e.created_at)}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATUS_BADGE[e.status]}>
                      {e.status}
                    </Badge>
                    {e.status === 'applied' && typeof e.points === 'number' && e.points > 0 && (
                      <div className="text-xs text-emerald-700 mt-1 font-medium">
                        +{e.points} pt{e.points === 1 ? '' : 's'}
                      </div>
                    )}
                    {e.fixed_at && (
                      <div className="text-xs text-emerald-700 mt-1">fixed {relTime(e.fixed_at)}</div>
                    )}
                    {e.decision_notes && (
                      <div className="text-xs text-muted-foreground mt-1 italic">
                        "{e.decision_notes}"
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {e.status === 'open' && pickingForId !== e.id && (
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="default"
                          disabled={busyId === e.id}
                          onClick={() => setPickingForId(e.id)}
                        >
                          Accept…
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyId === e.id}
                          onClick={() => handleReject(e.id)}
                        >
                          Reject
                        </Button>
                      </div>
                    )}
                    {e.status === 'open' && pickingForId === e.id && (
                      <div className="space-y-1">
                        <div className="text-xs text-muted-foreground">Severity → points:</div>
                        <div className="flex gap-1 flex-wrap">
                          {SEVERITIES.map((s) => (
                            <Button
                              key={s.value}
                              size="sm"
                              variant="default"
                              disabled={busyId === e.id}
                              onClick={() => handleAcceptWithSeverity(e.id, s.value)}
                              title={`Award ${s.points} points`}
                            >
                              {s.label} +{s.points}
                            </Button>
                          ))}
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busyId === e.id}
                            onClick={() => setPickingForId(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                    {e.status !== 'open' && e.decided_by && (
                      <div className="text-xs text-muted-foreground">
                        by {e.decided_by}<br/>
                        {relTime(e.decided_at)}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
